// src/App.js
import React, { useState, useEffect } from 'react';
import Login from './Login';
import Sidebar from './Sidebar';
import Feed from './Feed';
import Profile from './Profile';
import AdminPanel from './AdminPanel';
import Schedule from './Schedule';
import Classrooms from './Classrooms';
import UsefulLinks from './UsefulLinks';
import Trash from './Trash';
import Logo from './Logo';
import LiveNow from './LiveNow';
import AppBanner from './AppBanner';
import DateWidget from './DateWidget';
import WeatherWidget from './WeatherWidget';
import SessionExpiredModal from './SessionExpiredModal';
import Settings from './Settings';
import CreatePostModal from './CreatePostModal';
import PublishSuccess from './PublishSuccess';
import { api, setSessionExpiredHandler } from './api';
import './App.css';
import './dark-generated.css';
import './theme.css';
import './mobile.css';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('access_token'));
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState('all');
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Увеличивается после любого изменения постов в админ-панели,
  // чтобы лента на главной обновилась сама.
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  // Окно «Создать пост» с главной и подтверждение после публикации
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [publishedInfo, setPublishedInfo] = useState(null);
  // После сохранения баннера в админ-панели верхняя полоса перечитывает его сама
  const [bannerRefreshKey, setBannerRefreshKey] = useState(0);

  useEffect(() => {
    if (isLoggedIn) {
      loadUser();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setShowSessionExpired(true);
    });
  }, []);

  const loadUser = async () => {
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      setUser({
        username: 'Учитель',
        role: 'teacher',
        email: '',
      });
    }
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  if (!isLoggedIn) {
    return (
      <Login 
        onLoginSuccess={() => setIsLoggedIn(true)} 
      />
    );
  }

  const isAdmin = user?.role === 'admin' || user?.is_admin || user?.is_superuser;

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setIsLoggedIn(false);
    setShowSessionExpired(false);
  };

  const handleReauthorize = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setShowSessionExpired(false);
    setIsLoggedIn(false);
  };

  return (
    <div className="app">
      {/* «Приложение в RuStore» — над шапкой, можно закрыть */}
      <AppBanner variant="top" refreshKey={bannerRefreshKey} />
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-brand">
            <button
              className="mobile-menu-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Меню"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
            <Logo size="small" />
            <span className="brand-text">Электронная учительская</span>
          </div>

          <div className="nav-center">
            <DateWidget />
            <WeatherWidget />
            <div className="search-box" style={{ position: 'relative' }}>
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Поиск..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <select
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: '0 8px',
                  fontSize: '13px',
                  color: 'var(--eu-text-muted)',
                  cursor: 'pointer',
                  outline: 'none',
                  marginLeft: '8px',
                  borderLeft: '1px solid var(--eu-border)',
                  paddingLeft: '12px'
                }}
              >
                <option value="all">Везде</option>
                <option value="posts">Посты</option>
                <option value="files">Файлы</option>
                <option value="sections">Разделы</option>
                <option value="subcategories">Подкатегории</option>
              </select>
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="nav-actions">
            {(user?.role === 'admin' || user?.is_admin || user?.is_superuser) && (
              <button className="admin-panel-button" onClick={() => setShowAdminPanel(true)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
                <span>Админ-панель</span>
              </button>
            )}

            <button
              className="settings-button"
              onClick={() => setShowSettings(true)}
              title="Настройки"
              aria-label="Настройки"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>

            <button className="profile-button" onClick={() => setShowProfile(true)} title="Профиль" aria-label="Профиль">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>

            <button className="logout-button" onClick={handleLogout}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>Выйти</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="app-content">
        <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}></div>
        <Sidebar
          selectedSection={selectedSection}
          selectedSubcategory={selectedSubcategory}
          onSectionSelect={(section) => {
            setSelectedSection(section);
            setSidebarOpen(false);
          }}
          onSubcategorySelect={(subcategory) => {
            setSelectedSubcategory(subcategory);
            setSidebarOpen(false);
          }}
          isOpen={sidebarOpen}
          user={user}
        />

        <main className="main-content">
          {selectedSection?.id === 'schedule' ? (
            <Schedule />
          ) : selectedSection?.id === 'classrooms' ? (
            <Classrooms user={user} />
          ) : selectedSection?.id === 'links' ? (
            <UsefulLinks user={user} />
          ) : selectedSection?.id === 'trash' ? (
            <Trash
              user={user}
              onPostRestore={(postId) => {}}
              onPostPermanentDelete={(postId) => {}}
            />
          ) : (
            <>
              {!selectedSection && !selectedSubcategory && !searchQuery && (
                <div className="welcome-banner">
                  <h1 className="welcome-title">Добро пожаловать в Электронную учительскую</h1>
                  <p className="welcome-subtitle">
                    Информационный школьный портал для сотрудников ГБОУ РШИ № 32
                  </p>
                </div>
              )}

              {/* Что идёт сейчас: урок/перемена и кабинеты — только на главной */}
              {!selectedSection && !selectedSubcategory && !searchQuery && <LiveNow />}

              <Feed
                selectedSection={selectedSection}
                selectedSubcategory={selectedSubcategory}
                searchQuery={searchQuery}
                searchFilter={searchFilter}
                user={user}
                refreshKey={feedRefreshKey}
                onCreatePost={isAdmin ? () => setShowCreatePost(true) : undefined}
              />
            </>
          )}
        </main>
      </div>

      {showProfile && (
        <Profile
          user={user}
          onClose={() => setShowProfile(false)}
          onUpdate={handleUserUpdate}
          onOpenAdminPanel={() => setShowAdminPanel(true)}
        />
      )}

      {showAdminPanel && (user?.role === 'admin' || user?.is_admin || user?.is_superuser) && (
        <AdminPanel
          onClose={() => setShowAdminPanel(false)}
          user={user}
          onPostsChanged={() => setFeedRefreshKey((key) => key + 1)}
          onBannerChanged={() => setBannerRefreshKey((key) => key + 1)}
        />
      )}

      {showCreatePost && isAdmin && (
        <CreatePostModal
          // Открыли внутри раздела или подраздела — они уже выбраны в форме,
          // с главной — выбираются вручную
          initialSection={selectedSection ? selectedSection.id : ''}
          initialSubcategory={selectedSubcategory ? selectedSubcategory.id : ''}
          onClose={() => setShowCreatePost(false)}
          onCreated={(info) => {
            setShowCreatePost(false);
            setPublishedInfo(info);
            setFeedRefreshKey((key) => key + 1);
          }}
        />
      )}

      {publishedInfo && (
        <PublishSuccess
          title={publishedInfo.title}
          sectionTitle={publishedInfo.sectionTitle}
          subcategoryTitle={publishedInfo.subcategoryTitle}
          onClose={() => setPublishedInfo(null)}
        />
      )}

      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}

      {showSessionExpired && (
        <SessionExpiredModal onReauthorize={handleReauthorize} />
      )}
    </div>
  );
}

export default App;
